/**
 * React Collection Row Widget
 * Horizontal scrolling collection display with snap behavior
 * Minimal, clean interface with optional navigation button
 */

(function () {
    'use strict';

    // Wait for React to be loaded via the promise
    function initWidget() {
        // Exit early if React is not available
        if (typeof React === 'undefined' || typeof ReactDOM === 'undefined') {
            console.error('React or ReactDOM not loaded for Collection Row Widget');
            return;
        }

        const { useState, useEffect, useRef, useCallback, createElement } = React;

        // JSX-like helper functions
        const jsx = (type, props, ...children) => {
            const flatChildren = children.flat().filter(child => child !== null && child !== undefined);
            return createElement(type, props, ...flatChildren);
        };

        // HTML element shortcuts
        const div = (props, ...children) => jsx('div', props, ...children);
        const span = (props, ...children) => jsx('span', props, ...children);
        const p = (props, ...children) => jsx('p', props, ...children);
        const h1 = (props, ...children) => jsx('h1', props, ...children);
        const h2 = (props, ...children) => jsx('h2', props, ...children);
        const h3 = (props, ...children) => jsx('h3', props, ...children);
        const h4 = (props, ...children) => jsx('h4', props, ...children);
        const img = (props) => jsx('img', props);
        const a = (props, ...children) => jsx('a', props, ...children);
        const button = (props, ...children) => jsx('button', props, ...children);

        // =============================================================================
        // UTILITIES
        // =============================================================================

        /**
         * Format price with currency
         */
        const formatPrice = (price, currency = 'USD') => {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(price / 100);
        };

        /**
         * Debounce function for performance
         */
        const debounce = (func, wait) => {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        };

        // =============================================================================
        // COMPONENTS
        // =============================================================================

        /**
         * SKU Filter Configuration (kept for size detection in product cards)
         * New format: SS-VP-4OZ-12PK or SS-[FLAVOR]-[SIZE]-[PACK]
         * NOTE: We only filter by SIZE (oz), not by product type (VP = Variety Pack)
         * This prevents duplicate cards for variety pack products
         */
        const SKU_FILTER_CONFIG = {
            '1.5 oz': ['1.5OZ', '1OZ'],
            '4 oz': ['4OZ']
            // 'Variety Pack': ['VP'] - REMOVED: VP is a product type, not a size
        };

        /**
         * Check if a SKU matches a pattern
         * New format uses contains logic for segment matching
         */
        const skuMatchesPattern = (sku, pattern) => {
            if (!sku || !pattern) return false;
            const upperSku = sku.toUpperCase();
            const upperPattern = pattern.toUpperCase();
            return upperSku.includes(upperPattern);
        };

        /**
         * Get SKU filter label for display purposes only (not for filtering)
         */
        const getSKUFilterForProduct = (product) => {
            if (!product.variants || product.variants.length === 0) return '';

            // Find first matching size category for display
            for (const [filterLabel, patterns] of Object.entries(SKU_FILTER_CONFIG)) {
                const hasMatch = product.variants.some(variant => {
                    if (!variant.sku) return false;
                    return patterns.some(pattern => skuMatchesPattern(variant.sku, pattern));
                });
                if (hasMatch) {
                    return filterLabel;
                }
            }

            return '';
        };

        /**
         * Check if a product/variant is a Variety Pack
         * Detects VP in the SKU to identify variety pack products
         */
        const isVarietyPack = (skuOrProduct) => {
            let sku = '';

            // Handle both SKU strings and product/variant objects
            if (typeof skuOrProduct === 'string') {
                sku = skuOrProduct;
            } else if (skuOrProduct?.sku) {
                sku = skuOrProduct.sku;
            } else if (skuOrProduct?.variants?.[0]?.sku) {
                sku = skuOrProduct.variants[0].sku;
            }

            return sku.toUpperCase().includes('VP');
        };

        /**
         * Product Card Component (Full version from main-collection-v2)
         * With flip functionality and variant selection
         */
        const ProductCard = ({ product, showVendor = false, showQuickBuy = true, showComparePrice = true }) => {
            const [isFlipped, setIsFlipped] = useState(false);
            const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
            const [isAddingToCart, setIsAddingToCart] = useState(false);

            // Set default selected variant to first available variant when product changes
            useEffect(() => {
                if (product.variants && product.variants.length > 0) {
                    setSelectedVariantIndex(0);
                }
            }, [product.id]);

            // Extract custom badge from p_badge_* tags
            const customBadge = product.tags?.find(tag => tag.startsWith('p_badge_'))?.replace('p_badge_', '') || null;

            const handleImageClick = (e) => {
                // Navigate to product page when clicking the image
                const selectedVariant = product.variants?.[selectedVariantIndex];
                const baseUrl = `/products/${product.handle}`;
                const url = selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                window.location.href = url;
            };

            const handleQuickShopClick = (e) => {
                // Flip the card when clicking the QUICK SHOP button
                e.preventDefault();
                e.stopPropagation();
                setIsFlipped(true);
            };

            const handleCloseFlip = (e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsFlipped(false);
            };

            const handleQuickAdd = async (e) => {
                e.preventDefault();
                e.stopPropagation();

                const selectedVariant = product.variants?.[selectedVariantIndex];
                if (!selectedVariant) {
                    alert('Please select a variant.');
                    return;
                }

                if (!selectedVariant.available) {
                    alert('Sorry, this variant is out of stock.');
                    return;
                }

                setIsAddingToCart(true);

                try {
                    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

                    let sectionsToBundle = ["cart-drawer"];
                    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
                        bubbles: true,
                        detail: { sections: sectionsToBundle }
                    }));

                    const formData = new FormData();
                    formData.set('id', selectedVariant.id);
                    formData.set('quantity', '1');
                    formData.set('sections', sectionsToBundle.join(","));
                    formData.set('sections_url', `${rootPath}variants/${selectedVariant.id}`);

                    const response = await fetch(`${rootPath}cart/add.js`, {
                        method: 'POST',
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: formData
                    });

                    const responseJson = await response.json();

                    if (response.ok) {
                        const cartContent = await (await fetch(`${rootPath}cart.js`)).json();
                        cartContent["sections"] = responseJson["sections"];

                        document.documentElement.dispatchEvent(new CustomEvent("variant:add", {
                            bubbles: true,
                            detail: {
                                items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
                                cart: cartContent
                            }
                        }));

                        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
                            bubbles: true,
                            detail: {
                                baseEvent: "variant:add",
                                cart: cartContent
                            }
                        }));

                        if (window.themeVariables && window.themeVariables.settings.cartType === "page") {
                            window.location.href = `${rootPath}cart`;
                        }
                    } else {
                        document.documentElement.dispatchEvent(new CustomEvent("cart:error", {
                            bubbles: true,
                            detail: {
                                error: responseJson["description"] || responseJson["message"]
                            }
                        }));

                        alert(responseJson.message || responseJson.description || 'Failed to add item to cart.');
                    }
                } catch (error) {
                    console.error('Cart add failed:', error);
                    alert('Failed to add item to cart. Please try again.');
                } finally {
                    setIsAddingToCart(false);
                }
            };

            return div({
                className: `product-card ${isFlipped ? 'product-card--flipped' : ''} ${product.variantSize ? 'product-card--variant' : ''}`,
                style: { perspective: '1000px' }
            },
                div({ className: 'product-card__inner' },
                    // FRONT SIDE
                    div({
                        className: 'product-card__front'
                    },
                        // Product image with badges (clickable - navigates to product page)
                        div({
                            className: 'product-card__figure',
                            onClick: handleImageClick,
                            style: { cursor: 'pointer' }
                        },
                            img({
                                src: (() => {
                                    // Try to get the selected variant's image first
                                    const selectedVariant = product.variants?.[selectedVariantIndex];
                                    if (selectedVariant?.image) {
                                        return selectedVariant.image;
                                    }
                                    // Fallback to product's featured image
                                    return product.featured_image;
                                })(),
                                alt: product.title,
                                className: 'product-card__image',
                                loading: 'lazy',
                                width: '300',
                                height: '300'
                            }),

                            // Badge container
                            div({ className: 'product-card__badge-list' },
                                customBadge && span({
                                    className: 'badge badge--custom',
                                    'aria-label': customBadge
                                }, customBadge.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                            )
                        ),

                        div({ className: 'product-card__header' },
                            // Product title with SKU filter (for display only)
                            p({ className: 'product-card__header-left' },
                                (() => {
                                    // For variant-specific products, show the variant size
                                    if (product.variantSize) {
                                        return `SUNFLOWER SEEDS | ${product.variantSize}`;
                                    }

                                    // Otherwise, extract SKU filter for the product (fallback)
                                    const skuFilter = getSKUFilterForProduct(product);
                                    const formattedSKUFilter = skuFilter ? ` | ${skuFilter}` : '';
                                    return `SUNFLOWER SEEDS${formattedSKUFilter}`;
                                })()
                            ),

                            // Bag count
                            (() => {
                                const SKU_BAG_MAPPING = {
                                    '6PK': '6',
                                    '11PK': '11', '12PK': '12',
                                    '14PK': '12 Bags + 2 Free', '36PK': '36', '54PK': '54'
                                };

                                const bagDisplays = product.variants?.map(variant => {
                                    const sku = variant.sku || '';
                                    const match = sku.toUpperCase().match(/(\d+PK)/);
                                    if (match) {
                                        const packSize = match[1];
                                        return SKU_BAG_MAPPING[packSize] || null;
                                    }
                                    return null;
                                }).filter(Boolean);

                                const uniqueBagDisplays = [...new Set(bagDisplays)];
                                let bagCountText = '';

                                if (uniqueBagDisplays.length === 1) {
                                    const singleDisplay = uniqueBagDisplays[0];
                                    bagCountText = (singleDisplay === '12 Bags + 2 Free') ? singleDisplay : `${singleDisplay} Bags`;
                                } else if (uniqueBagDisplays.length > 1) {
                                    const specialCases = uniqueBagDisplays.filter(item => item === '12 Bags + 2 Free');
                                    const regularCases = uniqueBagDisplays.filter(item => item !== '12 Bags + 2 Free');

                                    if (specialCases.length > 0) {
                                        if (regularCases.length === 0) {
                                            bagCountText = specialCases.join(' or ');
                                        } else if (regularCases.length === 1) {
                                            bagCountText = `${regularCases[0]} Bags or ${specialCases.join(' or ')}`;
                                        } else {
                                            const lastRegular = regularCases.pop();
                                            bagCountText = `${regularCases.join(', ')}, ${lastRegular} Bags or ${specialCases.join(' or ')}`;
                                        }
                                    } else {
                                        const lastNumber = uniqueBagDisplays.pop();
                                        bagCountText = uniqueBagDisplays.length > 0
                                            ? `${uniqueBagDisplays.join(', ')} or ${lastNumber} Bags`
                                            : `${lastNumber} Bags`;
                                    }
                                }

                                return bagCountText ? p({ className: 'product-card__header-right' }, bagCountText) : null;
                            })(),
                        ),

                        // Product information
                        div({ className: 'product-card__info' },
                            showVendor && product.vendor &&
                            p({ className: 'product-card__vendor' }, product.vendor),

                            h3({ className: 'product-card__title' }, product.title),

                            // Price information
                            div({ className: 'product-card__price' },
                                // Compare at price (strikethrough) - show if enabled and exists
                                (showComparePrice && product.compare_at_price && product.compare_at_price > product.price) &&
                                span({ className: 'price price--compare' }, formatPrice(product.compare_at_price)),
                                // Regular price
                                span({ className: 'price' }, formatPrice(product.price))
                            ),
                        ),

                        // Conditional buttons based on showQuickBuy setting
                        showQuickBuy ? (
                            // Quick Shop Mode: Show QUICK SHOP button + VIEW PRODUCT link
                            div({ className: 'product-card__bottom-wrapper' },
                                button({
                                    onClick: handleQuickShopClick,
                                    className: 'product-card__view-button',
                                    'aria-label': `Quick shop ${product.title}`
                                }, 'QUICK SHOP'),

                                a({
                                    href: (() => {
                                        const selectedVariant = product.variants?.[selectedVariantIndex];
                                        const baseUrl = `/products/${product.handle}`;
                                        return selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                                    })(),
                                    className: 'product-card__view-product-link product-card__view-product-link--front'
                                }, 'VIEW PRODUCT')
                            )
                        ) : (
                            // Simple Mode: Show SHOP NOW button only
                            div({ className: 'product-card__bottom-wrapper' },
                                a({
                                    href: (() => {
                                        const selectedVariant = product.variants?.[selectedVariantIndex];
                                        const baseUrl = `/products/${product.handle}`;
                                        return selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                                    })(),
                                    className: 'product-card__view-button'
                                }, 'SHOP NOW')
                            )
                        )
                    ),

                    // BACK SIDE
                    div({ className: 'product-card__back' },
                        // Header with close button
                        div({ className: 'product-card__back-header' },
                            h3({ className: 'product-card__back-title' },
                                (() => {
                                    // Show title with size information
                                    if (product.variantSize) {
                                        return `SUNFLOWER SEEDS | ${product.variantSize}`;
                                    }
                                    return 'SUNFLOWER SEEDS';
                                })()
                            ),
                            button({
                                className: 'product-card__close-btn',
                                onClick: handleCloseFlip,
                                'aria-label': 'Close details'
                            },
                                jsx('svg', {
                                    width: '12',
                                    height: '12',
                                    viewBox: '0 0 12 12',
                                    fill: 'none',
                                    xmlns: 'http://www.w3.org/2000/svg'
                                },
                                    jsx('path', {
                                        d: 'M1 1L11 11M11 1L1 11',
                                        stroke: 'currentColor',
                                        strokeWidth: '2',
                                        strokeLinecap: 'round'
                                    })
                                )
                            )
                        ),

                        // Product details
                        div({ className: 'product-card__back-content' },
                            h2({ className: 'product-card__back-product-title' }, product.title),

                            // Bag sizes row
                            (() => {
                                const SKU_BAG_MAPPING = {
                                    '6PK': '6',
                                    '11PK': '11', '12PK': '12',
                                    '14PK': '12 Bags + 2 Free', '36PK': '36', '54PK': 'Bucket Of 54 Bags'
                                };

                                if (!product.variants || product.variants.length === 0) return null;

                                const variantBagInfo = product.variants.map((variant, index) => {
                                    const sku = variant.sku || '';
                                    const match = sku.toUpperCase().match(/(\d+PK)/);
                                    if (match) {
                                        const packSize = match[1];
                                        const bagDisplay = SKU_BAG_MAPPING[packSize];
                                        if (bagDisplay) {
                                            const displayText = (bagDisplay === '12 Bags + 2 Free' || bagDisplay === 'Bucket Of 54 Bags')
                                                ? bagDisplay
                                                : `${bagDisplay} Bags`;
                                            return { index, variant, displayText, bagDisplay, packSize };
                                        }
                                    }
                                    return null;
                                }).filter(Boolean);

                                const uniqueVariantBagInfo = [];
                                const seenDisplayTexts = new Set();

                                variantBagInfo.forEach(info => {
                                    if (!seenDisplayTexts.has(info.displayText)) {
                                        seenDisplayTexts.add(info.displayText);
                                        uniqueVariantBagInfo.push(info);
                                    }
                                });

                                if (uniqueVariantBagInfo.length === 0) return null;

                                return div({ className: 'product-card__back-bag-sizes' },
                                    uniqueVariantBagInfo.map((info, displayIndex) => {
                                        const isSelected = selectedVariantIndex === info.index;
                                        const variantPrice = info.variant.price || 0;

                                        // Extract bag count from pack size
                                        const packSizeMatch = info.packSize.match(/(\d+)PK/);
                                        let totalOunces = 0;

                                        if (packSizeMatch) {
                                            const bagCount = parseInt(packSizeMatch[1]);

                                            // Determine ounces per bag from SKU
                                            const sku = info.variant.sku.toUpperCase();
                                            let ouncesPerBag = 1.5;

                                            if (sku.includes('4OZ')) {
                                                ouncesPerBag = 4;
                                            } else if (sku.includes('1.5OZ') || sku.includes('1OZ')) {
                                                ouncesPerBag = 1.5;
                                            }

                                            totalOunces = bagCount * ouncesPerBag;
                                        }

                                        const pricePerOz = totalOunces > 0 ?
                                            formatPrice(Math.round((variantPrice / totalOunces))) :
                                            formatPrice(0);

                                        return button({
                                            key: info.displayText,
                                            className: `product-card__back-bag-size ${isSelected ? 'product-card__back-bag-size--selected' : ''}`,
                                            onClick: () => setSelectedVariantIndex(info.index),
                                            'aria-pressed': isSelected,
                                            'aria-label': `Select ${info.displayText} variant`
                                        },
                                            div({ className: 'product-card__back-bag-size-content' },
                                                div({ className: 'product-card__back-bag-size-text' }, info.displayText),
                                                div({ className: 'product-card__back-bag-size-price' }, pricePerOz + ' / OZ')
                                            )
                                        );
                                    })
                                );
                            })(),

                            // Feature list
                            div({ className: 'product-card__back-features' },
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, 'FREE SHIPPING')
                                ),
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, '15% OFF EVERY ORDER')
                                ),
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, 'PAUSE, EDIT, OR CANCEL ANYTIME')
                                ),
                                div({ className: 'product-card__back-feature' },
                                    span({ className: 'product-card__back-feature-icon' }, '✕'),
                                    span({ className: 'product-card__back-feature-text' }, 'VIP DISCOUNTS AND PERKS')
                                )
                            ),
                        ),

                        // Large price display
                        div({ className: 'product-card__back-large-price' },
                            (() => {
                                const selectedVariant = product.variants?.[selectedVariantIndex] || product.variants?.[0];
                                const variantPrice = selectedVariant?.price || product.price;
                                return formatPrice(variantPrice);
                            })()
                        ),

                        // Action buttons
                        div({ className: 'product-card__back-actions' },
                            button({
                                className: 'product-card__add-to-cart-btn',
                                onClick: handleQuickAdd,
                                disabled: isAddingToCart,
                                'aria-busy': isAddingToCart
                            }, isAddingToCart ? 'ADDING...' : 'ADD TO CART'),

                            a({
                                href: (() => {
                                    const selectedVariant = product.variants?.[selectedVariantIndex];
                                    const baseUrl = `/products/${product.handle}`;
                                    return selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
                                })(),
                                className: 'product-card__view-product-link'
                            }, 'VIEW PRODUCT')
                        )
                    )
                )
            );
        };

        /**
         * Section Header Component
         * Customizable title for each collection row
         */
        const SectionHeader = ({ title, titleSize, alignment, showTitle }) => {
            if (!showTitle || !title) return null;

            const TitleTag = titleSize || 'h2';
            const className = `collection-row__title collection-row__title--${alignment || 'left'} ${titleSize || 'h2'}`;

            return jsx(TitleTag, { className }, title);
        };

        /**
         * Navigation Button Component
         * Modern buttons for scrolling through products
         */
        const NavigationButton = ({ scrollContainerRef, scrollDistance = 80, isAtEnd, direction = 'next' }) => {
            const [isScrolling, setIsScrolling] = useState(false);

            const handleScroll = useCallback(() => {
                if (!scrollContainerRef.current || isScrolling) return;

                const container = scrollContainerRef.current;
                const scrollAmount = (container.clientWidth * scrollDistance) / 100;
                const scrollDirection = direction === 'back' ? -scrollAmount : scrollAmount;

                setIsScrolling(true);
                container.scrollBy({
                    left: scrollDirection,
                    behavior: 'smooth'
                });

                // Reset scrolling state after animation
                setTimeout(() => setIsScrolling(false), 500);
            }, [scrollContainerRef, scrollDistance, isScrolling, direction]);

            const isDisabled = direction === 'next' ? isAtEnd : false;
            const arrowIcon = direction === 'back' ? '←' : '→';
            const label = direction === 'back' ? 'Scroll to previous products' : 'Scroll to next products';

            return button({
                className: `collection-row__nav-btn collection-row__nav-btn--${direction} ${isDisabled ? 'collection-row__nav-btn--disabled' : ''}`,
                onClick: handleScroll,
                disabled: isDisabled || isScrolling,
                'aria-label': label
            },
                span({ className: 'collection-row__nav-arrow' }, arrowIcon)
            );
        };

        /**
         * Infinite Scroll Observer
         * Loads more products when approaching the end
         */
        const useInfiniteScroll = (containerRef, onLoadMore, hasMore) => {
            useEffect(() => {
                if (!containerRef.current || !hasMore) return;

                const options = {
                    root: containerRef.current,
                    rootMargin: '0px 200px 0px 0px', // Start loading 200px before the end
                    threshold: 0.1
                };

                const callback = (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && hasMore) {
                            onLoadMore();
                        }
                    });
                };

                const observer = new IntersectionObserver(callback, options);

                // Observe the last few products
                const products = containerRef.current.querySelectorAll('.collection-row-product');
                const lastProducts = Array.from(products).slice(-3);
                lastProducts.forEach(product => observer.observe(product));

                return () => observer.disconnect();
            }, [containerRef, onLoadMore, hasMore]);
        };

        /**
         * Main Collection Row Widget Component
         */
        const CollectionRowWidget = ({ config }) => {
            const scrollContainerRef = useRef(null);
            const [products, setProducts] = useState(config.initialProducts || []);
            const [isAtEnd, setIsAtEnd] = useState(false);
            const [showBackButton, setShowBackButton] = useState(false);
            const [hasMoreProducts, setHasMoreProducts] = useState(true);
            const [isLoadingMore, setIsLoadingMore] = useState(false);

            // Configuration
            const {
                sectionId,
                collectionHandle,
                productsToShow,
                showNavigationButton = true,
                enableSnapScroll = true,
                scrollDistance = 80,
                sectionTitle,
                titleSize,
                titleAlignment,
                showTitle = true,
                cardSettings = {}
            } = config;

            // Handle scroll to detect if at end and if back button should show
            const handleScroll = useCallback(() => {
                if (!scrollContainerRef.current) return;

                const container = scrollContainerRef.current;
                const { scrollLeft, scrollWidth, clientWidth } = container;

                // Check if at end
                const isNearEnd = scrollLeft + clientWidth >= scrollWidth - 10;
                setIsAtEnd(isNearEnd);

                // Show back button only if scrolled past first item (more than 50px from start)
                setShowBackButton(scrollLeft > 50);
            }, []);

            // Load more products (infinite scroll)
            const loadMoreProducts = useCallback(async () => {
                if (isLoadingMore || !hasMoreProducts) return;

                setIsLoadingMore(true);

                try {
                    // Fetch more products via AJAX
                    const response = await fetch(`/collections/${collectionHandle}/products.json?limit=10&page=${Math.ceil(products.length / 10) + 1}`);
                    const data = await response.json();

                    if (data.products && data.products.length > 0) {
                        // Transform products to match our format
                        const newProducts = data.products.map(product => ({
                            id: product.id,
                            handle: product.handle,
                            title: product.title,
                            vendor: product.vendor,
                            price: product.variants[0].price * 100, // Convert to cents
                            available: product.available,
                            featured_image: product.images[0] || '',
                            images: product.images.slice(0, 2),
                            variants: product.variants.slice(0, 1).map(v => ({
                                id: v.id,
                                title: v.title,
                                sku: v.sku,
                                price: v.price * 100,
                                available: v.available
                            })),
                            tags: product.tags
                        }));

                        setProducts(prev => [...prev, ...newProducts]);
                    } else {
                        setHasMoreProducts(false);
                    }
                } catch (error) {
                    console.error('Error loading more products:', error);
                    setHasMoreProducts(false);
                } finally {
                    setIsLoadingMore(false);
                }
            }, [collectionHandle, products.length, isLoadingMore, hasMoreProducts]);

            // Setup infinite scroll
            useInfiniteScroll(scrollContainerRef, loadMoreProducts, hasMoreProducts && products.length < 100);

            // Add scroll event listener
            useEffect(() => {
                const container = scrollContainerRef.current;
                if (!container) return;

                container.addEventListener('scroll', handleScroll);
                return () => container.removeEventListener('scroll', handleScroll);
            }, [handleScroll]);

            // Render nothing if no products
            if (!products || products.length === 0) {
                return div({ className: 'collection-row-empty' }, 'No products available');
            }

            return div({ className: `collection-row-widget ${!cardSettings.showQuickBuy ? 'collection-row-widget--no-quick-buy' : ''}` },
                // Section header
                jsx(SectionHeader, {
                    title: sectionTitle,
                    titleSize,
                    alignment: titleAlignment,
                    showTitle
                }),

                // Main content wrapper
                div({ className: 'collection-row-content' },
                    // Scroll container
                    div({
                        ref: scrollContainerRef,
                        className: `collection-row-scroll ${enableSnapScroll ? 'collection-row-scroll--snap' : ''}`,
                        'aria-label': 'Product carousel'
                    },
                        products.map(product =>
                            jsx(ProductCard, {
                                key: product.id,
                                product,
                                showVendor: cardSettings.showVendor,
                                showQuickBuy: cardSettings.showQuickBuy,
                                showComparePrice: cardSettings.showComparePrice
                            })
                        ),

                        // Loading indicator (hidden)
                        isLoadingMore && div({ className: 'collection-row-loading' }, 'Loading...')
                    ),

                    // Navigation buttons
                    showNavigationButton && [
                        // Back button - only show when scrolled past first item
                        showBackButton && jsx(NavigationButton, {
                            key: 'back-button',
                            scrollContainerRef,
                            scrollDistance,
                            direction: 'back'
                        }),
                        // Next button
                        jsx(NavigationButton, {
                            key: 'next-button',
                            scrollContainerRef,
                            scrollDistance,
                            isAtEnd,
                            direction: 'next'
                        })
                    ]
                )
            );
        };

        // =============================================================================
        // INITIALIZATION
        // =============================================================================

        /**
         * Initialize all collection row widgets on the page
         */
        const initializeWidgets = () => {
            // Find all configuration scripts
            const configScripts = Array.from(document.querySelectorAll('script[type="application/json"]'))
                .filter(script => {
                    return Array.from(script.attributes).some(attr =>
                        attr.name.startsWith('data-collection-row-config-')
                    );
                });

            configScripts.forEach(script => {
                try {
                    const config = JSON.parse(script.textContent);
                    const container = document.getElementById(config.containerId);

                    if (!container) {
                        console.warn(`Container not found for collection row: ${config.containerId}`);
                        return;
                    }

                    // Create React root and render widget
                    const root = ReactDOM.createRoot(container);
                    root.render(jsx(CollectionRowWidget, { config }));

                } catch (error) {
                    console.error('Error initializing collection row widget:', error);
                }
            });
        };

        // Initialize when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initializeWidgets);
        } else {
            // DOM is already loaded
            setTimeout(initializeWidgets, 0);
        }

        // Expose to global scope for debugging
        window.CollectionRowWidget = CollectionRowWidget;
    }

    // Wait for React to load (it's loaded globally in theme.liquid)
    // Widgets always run with defer, so React will be available
    if (typeof React !== 'undefined' && typeof ReactDOM !== 'undefined') {
        initWidget();
    } else {
        console.error('Collection Row Widget: React not available. Ensure React is loaded in theme.liquid');
    }
})();